import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo } from 'react';
import { getCurrentColorScheme, applyColorScheme as applyCustomSchemeVariables } from '../services/themeService';

/**
 * THEME MANAGEMENT SYSTEM
 * ----------------------
 * This context provides theme switching functionality between light and dark modes.
 * It works with the CSS variables defined in src/index.css and Tailwind's darkMode: 'class'.
 * 
 * HOW IT WORKS:
 * 1. ThemeProvider adds/removes the 'dark' class to the HTML root element
 * 2. CSS variables in index.css have different values for light/dark modes
 * 3. Tailwind uses these CSS variables through the theme config in tailwind.config.js
 * 4. User preference is stored in localStorage for persistence
 * 
 * USAGE:
 * - Wrap your app with <ThemeProvider>
 * - Use useTheme() hook to access theme state and controls
 * - Call toggleTheme() to switch between modes
 * - Call setTheme('dark'|'light') to set a specific mode
 */

/**
 * Defines the possible theme values.
 */
type SystemTheme = 'system';
type AppTheme = 'light' | 'dark';
type Theme = AppTheme | SystemTheme;

/**
 * Defines the shape of the context value provided by ThemeContext.
 */
interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Props for the ThemeProvider component.
 */
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

/**
 * Provides theme state and functions to its children components.
 * It handles theme persistence in localStorage and applies the theme
 * class to the document's root element for CSS (e.g., Tailwind dark mode).
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const storedTheme = localStorage.getItem(storageKey) as Theme | null;
      if (storedTheme) {
        return storedTheme;
      }
      // No need to check defaultTheme === 'system' here for initial state,
      // as it's handled by the effect. Just return defaultTheme.
      return defaultTheme;
    } catch (e) {
      // Fallback if localStorage is unavailable or other errors
      return defaultTheme;
    }
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    let effectiveMode: AppTheme;
    if (theme === 'system') {
      effectiveMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      effectiveMode = theme;
    }
    
    root.classList.add(effectiveMode);

    // Apply colors from the current custom scheme
    const currentScheme = getCurrentColorScheme(); // This gets either saved or default
    applyCustomSchemeVariables(currentScheme, effectiveMode); // Apply the variables for the effectiveMode

    try {
      localStorage.setItem(storageKey, theme);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [theme, storageKey]);

  const toggleTheme = () => {
    setThemeState((prevTheme) => {
      if (prevTheme === 'system') {
        // If current is system, toggle based on actual resolved theme
        const currentActualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        return currentActualTheme === 'light' ? 'dark' : 'light';
      }
      return prevTheme === 'light' ? 'dark' : 'light';
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };
  
  const contextValue = useMemo(() => ({
    theme,
    toggleTheme,
    setTheme,
  }), [theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to access the theme context.
 * @returns {ThemeContextType} The current theme and theme manipulation functions.
 * @throws {Error} If used outside of a ThemeProvider.
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
